// AdminShell (DESIGN §3.2.1, N6): AdminNav 248 (lg+) → TopBar → main (ohne Verlaufslinie, Kennung = Badge)
// (Rand 24, max. 1440). Mobil (< lg): Topbar 56 mit Menü-Knopf, Monogramm mit Badge, Titel;
// Navigation als Drawer von links. Kein Chat-Dock, kein Chat-Icon (UX §2.7).
import { List } from '@phosphor-icons/react';
import { type ReactNode, useState } from 'react';
import { SkipLink } from '../kern/Misc';
import { Monogram, PlatformBadge } from '../kern/Monogram';
import { PageChromeProvider } from '../kern/page-chrome';
import { Drawer, ShellIconButton, TooltipProvider } from './parts';
import { TopBar, type TopBarProps } from './TopBar';

export interface AdminShellProps {
  /** AdminNav; `onNavigate` schließt den Drawer nach einer Wahl. */
  nav: (onNavigate: () => void) => ReactNode;
  topbar: Omit<TopBarProps, 'leading' | 'size'>;
  /** Wechselt bei jeder Navigation (Pfad), schließt den Drawer. */
  navKey?: string;
  appName?: string;
  banner?: ReactNode;
  overlays?: ReactNode;
  children: ReactNode;
}

export function AdminShell({
  nav,
  topbar,
  navKey,
  appName = 'Bar',
  banner,
  overlays,
  children,
}: AdminShellProps) {
  // Der Drawer merkt sich den Pfad, auf dem er geöffnet wurde; jede Navigation schließt ihn.
  const [openAt, setOpenAt] = useState<string | null>(null);
  const drawer = openAt === (navKey ?? '');
  const setDrawer = (open: boolean) => setOpenAt(open ? (navKey ?? '') : null);
  const close = () => setDrawer(false);
  return (
    <TooltipProvider delayDuration={400}>
      <PageChromeProvider>
        <div data-shell="admin" className="min-h-dvh bg-page text-fg">
          <SkipLink />
          {banner}
          <aside className="fixed inset-y-0 left-0 z-(--bar-z-nav) hidden w-admin-nav lg:block">
            {nav(close)}
          </aside>
          <Drawer open={drawer} onOpenChange={setDrawer} side="left" title="Admin navigation">
            {nav(close)}
          </Drawer>
          <div className="flex min-h-dvh min-w-0 flex-col lg:pl-admin-nav">
            <div className="px-4 lg:px-6">
              <TopBar
                {...topbar}
                size="people"
                leading={
                  <span className="-ml-2 flex shrink-0 items-center gap-1.5 lg:hidden">
                    <ShellIconButton
                      icon={List}
                      label="Open navigation"
                      tone="fg"
                      aria-haspopup="dialog"
                      aria-expanded={drawer}
                      onClick={() => setDrawer(true)}
                    />
                    {/* Badge „Admin“ neben dem Monogramm statt Punkt (DESIGN §3.2.1). */}
                    <span className="inline-flex items-center gap-1">
                      <Monogram size={28} appName={appName} />
                      <PlatformBadge platform="admin" />
                    </span>
                  </span>
                }
              />
            </div>
            <main
              id="main"
              tabIndex={-1}
              className="mx-auto w-full max-w-page-max min-w-0 flex-1 px-4 pt-2 pb-10 outline-none lg:px-6"
            >
              {children}
            </main>
          </div>
          {overlays}
        </div>
      </PageChromeProvider>
    </TooltipProvider>
  );
}
